import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Shield, Phone, Mail, MapPin } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import '@/styles/animations.css'

export function PrivacyPolicyPage() {

  return (
    <div className="min-h-screen">
      <Header currentPage="privacy" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              <Shield className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Privacy <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
            <p className="text-sm text-gray-400 mt-4 animate-fade-in-up" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>Last updated: January 15, 2024</p>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Our Commitment to Privacy</h3>
              <p className="text-blue-800">
                Sri Sai Sabari Dental Clinic is committed to protecting your privacy and maintaining the confidentiality 
                of your personal and medical information in accordance with applicable laws and regulations.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
            <p className="text-gray-600 mb-6">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Personal identification information (name, email, phone number, address)</li>
              <li>Medical history and dental records</li>
              <li>Insurance information</li>
              <li>Appointment scheduling information</li>
              <li>Communication preferences</li>
              <li>Website usage data and cookies</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use your information for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Providing dental care and treatment services</li>
              <li>Scheduling and managing appointments</li>
              <li>Processing payments and insurance claims</li>
              <li>Communicating about your treatment and follow-up care</li>
              <li>Sending appointment reminders and health education materials</li>
              <li>Improving our services and website functionality</li>
              <li>Complying with legal and regulatory requirements</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>With your explicit consent</li>
              <li>For treatment purposes (referrals to specialists)</li>
              <li>For payment processing and insurance verification</li>
              <li>With healthcare operations partners (laboratories, pharmacies)</li>
              <li>As required by law or legal process</li>
              <li>To protect the rights and safety of our patients and staff</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate security measures to protect your information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Encrypted data transmission and storage</li>
              <li>Access controls and user authentication</li>
              <li>Regular security audits and updates</li>
              <li>Staff training on privacy and security procedures</li>
              <li>Secure disposal of paper and electronic records</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Your Rights</h2>
            <p className="text-gray-600 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Access your personal and medical information</li>
              <li>Request corrections to inaccurate information</li>
              <li>Request restrictions on use or disclosure</li>
              <li>Choose how you receive communications from us</li>
              <li>File a complaint if you believe your privacy rights have been violated</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Website Analytics</h2>
            <p className="text-gray-600 mb-4">
              Our website uses cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Remember your preferences and settings</li>
              <li>Analyze website traffic and usage patterns</li>
              <li>Improve user experience and website functionality</li>
              <li>Provide personalized content and recommendations</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Third-Party Services</h2>
            <p className="text-gray-600 mb-4">
              We may use third-party services that have their own privacy policies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Google Analytics for website analytics</li>
              <li>Social media platforms for communication</li>
              <li>Payment processing services</li>
              <li>Email marketing services</li>
              <li>Appointment scheduling platforms</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-600 mb-8">
              We retain your information for as long as necessary to provide services and comply with legal requirements. 
              Medical records are retained according to professional guidelines and applicable laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600 mb-8">
              We provide dental services to children with parental consent. We collect only necessary information 
              for treatment purposes and maintain strict confidentiality in accordance with applicable laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-600 mb-8">
              We may update this privacy policy periodically. We will notify you of any material changes by 
              posting the new policy on our website and updating the "last updated" date.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <p className="text-gray-600 mb-4">
                If you have questions about this privacy policy or how we handle your information, please contact us:
              </p>
              <div className="space-y-2 text-gray-600">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>+91-9841817510</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>smile@sssdental.com</span>
                </div>
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 mr-2 mt-1" />
                  <span>East Main Road, opposite to leobags, Puzhuthivakkam, Madipakkam, Chennai, Tamil Nadu 600091</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/terms-of-use">View Terms of Use</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/book-appointment">Book Appointment</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}